import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MetricTypes, metricKeys } from '@pulse/metrics';
import { exclusiveFrustrationSignalDimension, commonExcludeMetricList } from './defaultExcludes';
import { DASHBOARD_DATA_SCOPE, isAppModule } from '@utils/constants';
import { DIMENSION_TYPES_CONSTANTS } from '@utils/metrics';
import { DIMENSION_CATEGORY_KEYS } from '@utils/dimension/dimensionCategories';
import { PRESET_DASHBOARD_TYPE } from '@utils/presets/constants';
import { getValidPresetKeys } from '@utils/presets/utils';
import { FLOW_ERROR_ANALYSIS_ONLY_DIMENSIONS } from '@utils/dimension/appDimensionHelper';
import { metricsStore } from '@globalStore/metricsStore';
import { createFlowPresetLimitations } from './flowLimitation';
import { createFrustrationSignalPresetLimitations } from './frustrationSignalLimitation';
import { createEventPresetLimitations } from './eventLimitation';

const PRESET_KEYS = getValidPresetKeys();

export const defaultMetricConfig = {
  createCustomMetric: true,
  createFlowMetric: true,
  showPercentile: true,
  showFlow: true,
};

export const FLOW_ONLY_METRIC_CATEGORY_KEYS = ['global', 'flow', 'errors', 'network'];

// Decouple the limitation with preset config as preset config will be persisted in the db
// This will give us more flexibility to control the behavior of preset.
const createLimitation = () => {
  const { metricsCategoryKeys } = metricsStore.getState();
  const categoryKeys = metricsCategoryKeys;
  const flowOnlyMetricCategories = FLOW_ONLY_METRIC_CATEGORY_KEYS.map((key) => categoryKeys[key]);

  const DEFAULT_LIMITATION = {
    metrics: {
      excludeTypes: [],
      excludeKeys: [...commonExcludeMetricList],
      excludeCategories: [...flowOnlyMetricCategories, categoryKeys.ECO_SIGNAL_EVENT],
      config: { ...defaultMetricConfig },
    },
    dimensions: {
      excludeCategories: [...exclusiveFrustrationSignalDimension],
    },
  };

  const DPI_OVERVIEW_METRIC_PANEL_LIMITATION = {
    metrics: {
      excludeTypes: [],
      excludeKeys: [...commonExcludeMetricList],
      excludeCategories: [...flowOnlyMetricCategories, categoryKeys.ECO_SIGNAL_EVENT],
      config: {
        ...defaultMetricConfig,
        showFlow: false,
        createFlowMetric: false,
        createCustomMetric: false,
        showPercentile: true,
      },
    },
    dimensions: {
      excludeCategories: [...exclusiveFrustrationSignalDimension],
    },
  };

  const DPI_REALTIME_METRIC_PANEL_LIMITATION = {
    metrics: {
      excludeTypes: [],
      excludeKeys: [...commonExcludeMetricList],
      excludeCategories: [...flowOnlyMetricCategories, categoryKeys.ECO_SIGNAL_EVENT],
      config: {
        ...defaultMetricConfig,
        createFlowMetric: false,
        createCustomMetric: false,
        showPercentile: false,
      },
    },
    dimensions: {
      excludeCategories: [...exclusiveFrustrationSignalDimension],
    },
  };

  const APP_USER_EXPERIENCE_PRESET = {
    dataScope: DASHBOARD_DATA_SCOPE.SESSION,
    metrics: {
      excludeTypes: [],
      excludeKeys: [
        metricKeys.EventsPerUserHour,
        metricKeys.AppTotalEvents,
        ...commonExcludeMetricList,
      ],
      excludeCategories: [
        categoryKeys.ECO_ENGAGEMENT,
        categoryKeys.ECO_SERVICE_PERFORMANCE,
        categoryKeys.ECO_SIGNAL_EVENT,
        ...flowOnlyMetricCategories,
      ],
      config: {
        ...defaultMetricConfig,
      },
    },
    dimensions: {
      excludeTypes: [
        DIMENSION_TYPES_CONSTANTS.mappedEventName,
        DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata,
      ],
      excludeKeys: [...FLOW_ERROR_ANALYSIS_ONLY_DIMENSIONS],
      excludeCategories: [
        DIMENSION_CATEGORY_KEYS.eco.event_tags,
        ...exclusiveFrustrationSignalDimension,
      ],
    },
    summaryMetricPanel: {
      SPIWidget: {},
    },
    dimensionPanel: {
      networkRequestTab: {
        disabled: true,
      },
    },
  };

  const APP_EVENTS_PRESET = {
    dataScope: DASHBOARD_DATA_SCOPE.EVENT,
    metrics: {
      excludeTypes: [MetricTypes.appSpecificMetrics],
      excludeKeys: [
        metricKeys.AverageRequestDuration,
        metricKeys.AverageRequestCount,
        metricKeys.FailedNetworkRequest,
        metricKeys.AppVideoBounceRate,
        metricKeys.AverageMinutesPerPage,
        metricKeys.TimeToFirstAttempt,
        metricKeys.UserActiveTime,
        metricKeys.user_session_active_time,
        metricKeys.user_session_count_ended,
        metricKeys.EndedSessionCount,
        metricKeys.EventsPerUserHour,
        metricKeys.AvgDNSLookupTime,
        metricKeys.AvgDocumentResponseTime,
        metricKeys.AvgLargestContentfulPaintTime,
        metricKeys.DocumentLoadTime,
        metricKeys.avg_cvv_soft_navigation_time,
        metricKeys.ExitsBeforeAppLaunch,
        metricKeys.HotAppLaunches,
        metricKeys.DocumentLoads,
        metricKeys.page_views,
        metricKeys.avg_cvv_soft_navigation_time,
      ],
      excludeCategories: [
        categoryKeys.ECO_ENGAGEMENT,
        categoryKeys.ECO_QOE,
        categoryKeys.ECO_SERVICE_PERFORMANCE,
        categoryKeys.ECO_FRUSTRATION_SIGNALS,
        categoryKeys.ECO_SIGNAL_EVENT,
        categoryKeys.ECO_APP_USER_EXPERIENCE,
        ...flowOnlyMetricCategories,
      ],
      config: {
        ...defaultMetricConfig,
        showFlow: false,
        createFlowMetric: false,
        createCustomMetric: false,
      },
    },
    dimensions: {
      excludeKeys: [...FLOW_ERROR_ANALYSIS_ONLY_DIMENSIONS],
      excludeCategories: [
        DIMENSION_CATEGORY_KEYS.eco.network,
        ...exclusiveFrustrationSignalDimension,
      ],
    },
    dimensionPanel: {
      networkRequestTab: {
        disabled: true,
      },
    },
    summaryMetricPanel: {
      SPIWidget: {
        disabled: true,
      },
    },
  };

  const APP_NETWORK_PERFORMANCE_PRESET = {
    dataScope: DASHBOARD_DATA_SCOPE.EVENT,
    metrics: {
      excludeTypes: [MetricTypes.appSpecificMetrics],
      excludeKeys: [
        metricKeys.AppTotalEvents,
        metricKeys.EventsPerUserHour,
        metricKeys.AppCrashSessions,
        metricKeys.AppErrors,
        metricKeys.AppVideoBounceRate,
        metricKeys.AverageMinutesPerPage,
        metricKeys.TimeToFirstAttempt,
        metricKeys.UserActiveTime,
        metricKeys.user_session_active_time,
        metricKeys.EndedSessionCount,
      ],
      excludeCategories: [
        categoryKeys.ECO_ENGAGEMENT,
        categoryKeys.ECO_QOE,
        categoryKeys.ECO_WEB_USER_EXPERIENCE,
        categoryKeys.ECO_FRUSTRATION_SIGNALS,
        categoryKeys.ECO_SIGNAL_EVENT,
        categoryKeys.ECO_APP_USER_EXPERIENCE,
        categoryKeys.ECO_USER_DEFINED_METRICS,
        ...flowOnlyMetricCategories,
      ],
      config: {
        ...defaultMetricConfig,
        createCustomMetric: false,
        createFlowMetric: false,
        showFlow: false,
      },
    },
    dimensions: {
      excludeKeys: [...FLOW_ERROR_ANALYSIS_ONLY_DIMENSIONS],
      excludeCategories: [
        DIMENSION_CATEGORY_KEYS.eco.global_tags,
        DIMENSION_CATEGORY_KEYS.eco.events,
        ...exclusiveFrustrationSignalDimension,
      ],
    },
    dimensionPanel: {
      networkRequestTab: {},
    },
    summaryMetricPanel: {
      SPIWidget: {
        disabled: true,
      },
    },
  };

  const ECO_ENGAGEMENT_PRESET = {
    dataScope: DASHBOARD_DATA_SCOPE.SESSION,
    metrics: {
      excludeTypes: [],
      excludeKeys: [],
      excludeCategories: [
        categoryKeys.ECO_QOE,
        categoryKeys.ECO_USER_DEFINED_METRICS,
        categoryKeys.ECO_FRUSTRATION_SIGNALS,
        categoryKeys.ECO_SIGNAL_EVENT,
        ...flowOnlyMetricCategories,
      ],
      config: {
        ...defaultMetricConfig,
        createFlowMetric: false,
        showFlow: false,
      },
    },
    dimensions: {
      excludeKeys: [...FLOW_ERROR_ANALYSIS_ONLY_DIMENSIONS],
      excludeCategories: [...exclusiveFrustrationSignalDimension],
    },
    dimensionPanel: {
      networkRequestTab: {
        disabled: true,
      },
    },
    summaryMetricPanel: {
      SPIWidget: {
        disabled: true,
      },
    },
  };

  const APP_WEB_EXPERIENCE_PRESET = {
    dataScope: DASHBOARD_DATA_SCOPE.SESSION,
    metrics: {
      excludeTypes: [],
      excludeKeys: [
        metricKeys.AppTotalEvents,
        metricKeys.EventsPerUserHour,
        metricKeys.anr_count,
        metricKeys.user_session_with_anr,
        metricKeys.user_session_with_anr_percentage,
        metricKeys.anr_to_app_startup_success,
        metricKeys.anr_to_app_startup_success_rate,
        metricKeys.crash_to_app_startup_success,
        metricKeys.crash_to_app_startup_success_rate,
        metricKeys.AppCrashSessions,
        metricKeys.app_crash_to_app_startup_success,
        metricKeys.app_crash_to_app_startup_success_rate,
        metricKeys.app_crash_to_session_end_success,
        metricKeys.app_crash_to_session_end_init,
        metricKeys.app_crash_to_session_end_success_rate,
        metricKeys.excessive_app_restart_count,
        metricKeys.anr_to_session_end_success,
        metricKeys.mins_with_anr_to_app_startup,
        metricKeys.mins_with_crash_to_app_startup,
        metricKeys.user_session_with_app_restart,
        metricKeys.user_session_with_app_restart_percentage,
        metricKeys.excessive_app_restart_count,
        metricKeys.users_with_app_restart,
        metricKeys.users_with_app_restart_percentage,
        metricKeys.user_session_with_app_crash,
        metricKeys.user_session_with_app_crash_percentage,
        metricKeys.users_with_anr,
        metricKeys.users_with_anr_percentage,
        ...commonExcludeMetricList,
      ],
      excludeCategories: [
        categoryKeys.ECO_ENGAGEMENT,
        categoryKeys.ECO_QOE,
        categoryKeys.ECO_SERVICE_PERFORMANCE,
        categoryKeys.ECO_APP_USER_EXPERIENCE,
        categoryKeys.ECO_SIGNAL_EVENT,
        categoryKeys.ECO_USER_DEFINED_METRICS,
        ...flowOnlyMetricCategories,
      ],
      config: {
        ...defaultMetricConfig,
        showFlow: false,
      },
    },
    dimensions: {
      excludeTypes: [
        DIMENSION_TYPES_CONSTANTS.mappedEventName,
        DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata,
      ],
      excludeKeys: [...FLOW_ERROR_ANALYSIS_ONLY_DIMENSIONS],
      excludeCategories: [
        DIMENSION_CATEGORY_KEYS.eco.event_tags,
        DIMENSION_CATEGORY_KEYS.eco.screen,
        DIMENSION_CATEGORY_KEYS.eco.global_tags,
        ...exclusiveFrustrationSignalDimension,
      ],
    },
    dimensionPanel: {
      networkRequestTab: {
        disabled: true,
      },
    },
  };

  const APP_MOBILE_EXPERIENCE_PRESET = {
    dataScope: DASHBOARD_DATA_SCOPE.SESSION,
    metrics: {
      excludeTypes: [],
      excludeKeys: [
        metricKeys.AppTotalEvents,
        metricKeys.EventsPerUserHour,
        metricKeys.users_with_page_loop,
        metricKeys.users_with_page_loop_percentage,
        metricKeys.user_session_with_page_loop,
        metricKeys.user_session_with_page_loop_percentage,
        metricKeys.page_loop_count,
        ...commonExcludeMetricList,
      ],
      excludeCategories: [
        categoryKeys.ECO_ENGAGEMENT,
        categoryKeys.ECO_QOE,
        categoryKeys.ECO_SERVICE_PERFORMANCE,
        categoryKeys.ECO_WEB_USER_EXPERIENCE,
        categoryKeys.ECO_SIGNAL_EVENT,
        categoryKeys.ECO_USER_DEFINED_METRICS,
        ...flowOnlyMetricCategories,
      ],
      config: {
        ...defaultMetricConfig,
        showFlow: false,
      },
    },
    dimensions: {
      excludeTypes: [
        DIMENSION_TYPES_CONSTANTS.mappedEventName,
        DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata,
      ],
      excludeKeys: [...FLOW_ERROR_ANALYSIS_ONLY_DIMENSIONS],
      excludeCategories: [
        DIMENSION_CATEGORY_KEYS.eco.event_tags,
        DIMENSION_CATEGORY_KEYS.eco.page,
        DIMENSION_CATEGORY_KEYS.eco.global_tags,
        DIMENSION_CATEGORY_KEYS.eco.campaign,
        ...exclusiveFrustrationSignalDimension,
      ],
    },
    dimensionPanel: {
      networkRequestTab: {
        disabled: true,
      },
    },
  };

  return isAppModule
    ? {
        default: DEFAULT_LIMITATION,

        overview: DPI_OVERVIEW_METRIC_PANEL_LIMITATION,

        realtime: DPI_REALTIME_METRIC_PANEL_LIMITATION,

        // User Experience - session scope
        [PRESET_KEYS.APP_USER_EXPERIENCE]: APP_USER_EXPERIENCE_PRESET,

        // Web Experience - session scope
        [PRESET_KEYS.APP_WEB_EXPERIENCE]: APP_WEB_EXPERIENCE_PRESET,

        // Mobile Experience - session scope
        [PRESET_KEYS.APP_MOBILE_EXPERIENCE]: APP_MOBILE_EXPERIENCE_PRESET,

        // Events - event scope
        [PRESET_KEYS.APP_EVENTS]: APP_EVENTS_PRESET,

        // Network Performance - event scope
        [PRESET_KEYS.APP_NETWORK_PERFORMANCE]: APP_NETWORK_PERFORMANCE_PRESET,

        [PRESET_KEYS.ECO_ENGAGEMENT]: ECO_ENGAGEMENT_PRESET,

        [PRESET_DASHBOARD_TYPE.AI_ALERTS_DIAGNOSTICS_AverageRequestDuration]:
          APP_NETWORK_PERFORMANCE_PRESET,

        [PRESET_DASHBOARD_TYPE.RT_DRILLDOWN_AverageRequestDuration]: APP_NETWORK_PERFORMANCE_PRESET,

        ...createFlowPresetLimitations(),
        ...createFrustrationSignalPresetLimitations(),
        ...createEventPresetLimitations(),
      }
    : { default: DEFAULT_LIMITATION };
};

type State = {
  limitations: ReturnType<typeof createLimitation>;
};

type Action = {
  setLimitations: () => void;
};

const initialState = {
  limitations: createLimitation(),
};

const limitationStore = create(
  devtools<State & Action>(
    (set) => ({
      ...initialState,
      setLimitations: () =>
        set(() => {
          const limitations = createLimitation();
          return { limitations };
        }),
    }),
    { name: 'limitationStore' },
  ),
);

export default limitationStore;
